import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CHAPTERS, SCENES, Scene, Choice } from '../data/questData';
import { loadXP, addXP, setCharacter, XPState } from '../data/xp';
import { syncProgress } from '../api/public';
import ModuleSummary from '../components/ModuleSummary';
import { recordStart } from '../data/progress';

type Phase = 'character' | 'chapterSelect' | 'playing' | 'result';

const CHARACTERS = [
  { id: 'gamer' as const, emoji: '🎮', name: 'The Gamer', desc: 'Online 24/7. Knows every platform, every game. But do you know who\'s watching you play?' },
  { id: 'influencer' as const, emoji: '📸', name: 'The Influencer', desc: 'Building an audience, posting constantly. Every like costs something — your data.' },
  { id: 'student' as const, emoji: '📚', name: 'The Student', desc: 'Using school apps, researching online, figuring it all out. Is your school protecting you?' },
];

function PrivacyScoreDots({ score }: { score: number }) {
  const normalised = Math.min(5, Math.max(0, score + 2)); // -2..+2 → 0..4
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {[0,1,2,3,4].map(i => (
        <div key={i} style={{
          width: 10, height: 10, borderRadius: '50%',
          background: i < normalised ? '#10b981' : i < 3 && normalised < 3 ? '#f43f5e' : '#e5e7eb',
          transition: 'background 0.3s',
        }} />
      ))}
    </div>
  );
}

export default function PrivacyQuest() {
  const navigate = useNavigate();
  const [xp, setXp] = useState<XPState>(loadXP());
  const [phase, setPhase] = useState<Phase>(xp.character ? 'chapterSelect' : 'character');
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [privacyScore, setPrivacyScore] = useState(0);
  const [earnedXP, setEarnedXP] = useState(0);
  const [lastChoice, setLastChoice] = useState<Choice | null>(null);
  const [showOutcome, setShowOutcome] = useState(false);

  const character = CHARACTERS.find(c => c.id === xp.character) || CHARACTERS[0];

  function pickCharacter(id: typeof CHARACTERS[0]['id']) {
    const updated = setCharacter(id);
    setXp(updated);
    setPhase('chapterSelect');
  }

  function startChapter(chapterId: number) {
    setSelectedChapter(chapterId);
    setSceneIndex(0);
    setPrivacyScore(0);
    setEarnedXP(0);
    setLastChoice(null);
    setShowOutcome(false);
    setPhase('playing');
  }

  function makeChoice(choice: Choice) {
    setLastChoice(choice);
    setShowOutcome(true);
    setPrivacyScore(s => s + choice.privacyScore);
    setEarnedXP(e => e + choice.xpReward);
  }

  function continueStory() {
    setShowOutcome(false);
    const chapter = CHAPTERS.find(c => c.id === selectedChapter)!;
    if (sceneIndex + 1 < chapter.scenes.length) {
      setSceneIndex(i => i + 1);
      setLastChoice(null);
    } else {
      // chapter complete
      const moduleId = `quest_chapter_${selectedChapter}`;
      const totalXP = earnedXP + (lastChoice?.xpReward || 0);
      const updated = addXP(totalXP, moduleId);
      setXp(updated);
      recordStart();
      syncProgress(moduleId, totalXP);
      setPhase('result');
    }
  }

  const currentChapter = CHAPTERS.find(c => c.id === selectedChapter);
  const currentSceneId = currentChapter?.scenes[sceneIndex];
  const currentScene: Scene | undefined = currentSceneId ? SCENES[currentSceneId] : undefined;

  // ── Character selection ──────────────────────────────────────────────────
  if (phase === 'character') return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '48px 24px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#6366f1', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Privacy Quest</div>
          <h1 style={{ fontSize: 36, fontWeight: 800, color: '#1e1b4b', marginBottom: 12 }}>Choose your character</h1>
          <p style={{ color: '#6b7280', fontSize: 16 }}>Your identity shapes the scenarios you'll face.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          {CHARACTERS.map(c => (
            <button key={c.id} onClick={() => pickCharacter(c.id)} style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: 20,
              padding: 28,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
              onMouseEnter={e => { const el = e.currentTarget; el.style.border = '2px solid #6366f1'; el.style.transform = 'translateY(-4px)'; }}
              onMouseLeave={e => { const el = e.currentTarget; el.style.border = '2px solid #e5e7eb'; el.style.transform = ''; }}
            >
              <div style={{ fontSize: 48, marginBottom: 12 }}>{c.emoji}</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e1b4b', marginBottom: 8 }}>{c.name}</h3>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5 }}>{c.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Chapter selection ────────────────────────────────────────────────────
  if (phase === 'chapterSelect') return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '48px 24px' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
          <span style={{ fontSize: 32 }}>{character.emoji}</span>
          <div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Playing as</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#1e1b4b' }}>{character.name}</div>
          </div>
          <button onClick={() => setPhase('character')} style={{ marginLeft: 'auto', fontSize: 13, color: '#6366f1', background: 'none', border: '1px solid #c7d2fe', borderRadius: 8, padding: '6px 14px', cursor: 'pointer' }}>
            Change character
          </button>
        </div>

        <h2 style={{ fontSize: 28, fontWeight: 800, color: '#1e1b4b', marginBottom: 8 }}>Select a chapter</h2>
        <p style={{ color: '#6b7280', marginBottom: 28 }}>Each chapter = 3 scenes + XP rewards based on your choices.</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {CHAPTERS.map((ch, i) => {
            const done = xp.completedModules.includes(`quest_chapter_${ch.id}`);
            return (
              <button key={ch.id} onClick={() => startChapter(ch.id)} style={{
                background: 'white',
                border: `2px solid ${done ? '#10b981' : '#e5e7eb'}`,
                borderRadius: 16,
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: 20,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s',
              }}
                onMouseEnter={e => { const el = e.currentTarget; el.style.border = `2px solid ${done ? '#10b981' : '#6366f1'}`; el.style.transform = 'translateX(4px)'; }}
                onMouseLeave={e => { const el = e.currentTarget; el.style.border = `2px solid ${done ? '#10b981' : '#e5e7eb'}`; el.style.transform = ''; }}
              >
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: done ? '#d1fae5' : '#e0e7ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, fontWeight: 800, color: done ? '#059669' : '#3730a3',
                  flexShrink: 0,
                }}>
                  {done ? '✓' : i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#6366f1', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>{ch.tag}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1e1b4b' }}>Chapter {ch.id}: {ch.title}</div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{ch.scenes.length} scenes · up to 240 XP</div>
                </div>
                <span style={{ fontSize: 20, color: '#9ca3af' }}>→</span>
              </button>
            );
          })}
        </div>

        <button onClick={() => navigate('/')} style={{ marginTop: 24, fontSize: 14, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}>
          ← Back to home
        </button>
      </div>
    </div>
  );

  // ── Playing ──────────────────────────────────────────────────────────────
  if (phase === 'playing' && currentScene) {
    const totalScenes = currentChapter?.scenes.length || 3;
    return (
      <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '24px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          {/* Progress */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
            <button onClick={() => setPhase('chapterSelect')} style={{ fontSize: 13, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>← Chapters</button>
            <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${((sceneIndex) / totalScenes) * 100}%`, height: '100%', background: '#6366f1', transition: 'width 0.4s', borderRadius: 3 }} />
            </div>
            <span style={{ fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' }}>Scene {sceneIndex + 1} / {totalScenes}</span>
          </div>

          {/* Scene card */}
          <div style={{ background: 'white', borderRadius: 20, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #1e1b4b, #3730a3)', padding: '24px 28px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: '#818cf8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Ch.{currentScene.chapter} · {currentChapter?.title}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#c7d2fe' }}>Privacy score:</span>
                  <PrivacyScoreDots score={privacyScore} />
                </div>
              </div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: '#e0e7ff', margin: 0 }}>{currentScene.title}</h2>
            </div>

            <div style={{ padding: 28 }}>
              <p style={{ fontSize: 16, color: '#374151', lineHeight: 1.7, marginBottom: 20 }}>{currentScene.story}</p>

              {/* Situation box */}
              <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 12, padding: 16, marginBottom: 28 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>⚠ The Situation</div>
                <p style={{ fontSize: 15, color: '#78350f', lineHeight: 1.6, margin: 0 }}>{currentScene.situation}</p>
              </div>

              {/* Outcome reveal */}
              {showOutcome && lastChoice && (
                <div style={{
                  background: lastChoice.privacyScore >= 1 ? '#d1fae5' : lastChoice.privacyScore <= -1 ? '#fee2e2' : '#f3f4f6',
                  border: `1px solid ${lastChoice.privacyScore >= 1 ? '#6ee7b7' : lastChoice.privacyScore <= -1 ? '#fca5a5' : '#e5e7eb'}`,
                  borderRadius: 12,
                  padding: 20,
                  marginBottom: 20,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: lastChoice.privacyScore >= 1 ? '#065f46' : lastChoice.privacyScore <= -1 ? '#991b1b' : '#374151', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                    {lastChoice.privacyScore >= 1 ? '✓ Good choice' : lastChoice.privacyScore <= -1 ? '⚠ Privacy cost' : '→ Neutral'}
                    &nbsp;· +{lastChoice.xpReward} XP
                  </div>
                  <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.6, margin: 0 }}>{lastChoice.outcome}</p>
                  <button onClick={continueStory} style={{
                    marginTop: 16, background: '#3730a3', color: 'white', border: 'none', borderRadius: 10,
                    padding: '12px 24px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                  }}>
                    {sceneIndex + 1 < totalScenes ? 'Continue →' : 'Finish chapter →'}
                  </button>
                </div>
              )}

              {/* Choices */}
              {!showOutcome && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>What do you do?</div>
                  {currentScene.choices.map((choice, i) => (
                    <button key={i} onClick={() => makeChoice(choice)} style={{
                      background: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: 12,
                      padding: '14px 18px',
                      fontSize: 15,
                      color: '#1e1b4b',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.12s',
                      fontWeight: 500,
                      lineHeight: 1.5,
                    }}
                      onMouseEnter={e => { const el = e.currentTarget; el.style.border = '2px solid #6366f1'; el.style.background = '#eef2ff'; }}
                      onMouseLeave={e => { const el = e.currentTarget; el.style.border = '2px solid #e5e7eb'; el.style.background = 'white'; }}
                    >
                      <span style={{ fontWeight: 700, color: '#6366f1', marginRight: 8 }}>{String.fromCharCode(65 + i)}.</span>
                      {choice.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* XP earned this chapter */}
          {earnedXP > 0 && (
            <div style={{ textAlign: 'center', marginTop: 16, fontSize: 14, color: '#6b7280' }}>
              🏅 {earnedXP} XP earned this chapter so far
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Result ───────────────────────────────────────────────────────────────
  if (phase === 'result') {
    const grade = privacyScore >= 3 ? { label: 'Privacy Hero', color: '#10b981', emoji: '🏆' }
      : privacyScore >= 1 ? { label: 'Privacy Aware', color: '#6366f1', emoji: '⭐' }
      : privacyScore >= -1 ? { label: 'Learning', color: '#f59e0b', emoji: '📚' }
      : { label: 'Needs Work', color: '#f43f5e', emoji: '⚠️' };

    return (
      <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 540, margin: '0 auto' }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>{grade.emoji}</div>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: '#1e1b4b', marginBottom: 8 }}>Chapter Complete!</h1>
          <div style={{ fontSize: 22, fontWeight: 700, color: grade.color, marginBottom: 24 }}>{grade.label}</div>

          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 32 }}>
            <div style={{ background: 'white', borderRadius: 16, padding: '16px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#3730a3' }}>+{earnedXP}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>XP Earned</div>
            </div>
            <div style={{ background: 'white', borderRadius: 16, padding: '16px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#3730a3' }}>Lv.{xp.level}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Current Level</div>
            </div>
            <div style={{ background: 'white', borderRadius: 16, padding: '16px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginBottom: 4 }}>
                <PrivacyScoreDots score={privacyScore} />
              </div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>Privacy Score</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button onClick={() => setPhase('chapterSelect')} style={{
              background: '#3730a3', color: 'white', border: 'none', borderRadius: 12,
              padding: '14px 24px', fontSize: 16, fontWeight: 700, cursor: 'pointer',
            }}>
              Next chapter →
            </button>
            {xp.completedModules.filter(m => m.startsWith('quest_chapter')).length < CHAPTERS.length && (
              <button onClick={() => navigate('/')} style={{
                background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 12,
                padding: '14px 24px', fontSize: 16, fontWeight: 600, cursor: 'pointer',
              }}>
                ← Back to home
              </button>
            )}
          </div>
          {xp.completedModules.filter(m => m.startsWith('quest_chapter')).length >= CHAPTERS.length && (
            <ModuleSummary moduleId="quest" xpEarned={earnedXP} />
          )}
        </div>
      </div>
    );
  }

  return null;
}
